import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { useMaterialsList } from '../materials/useMaterials'
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type Line = { material_id: string; quantity: string; notes?: string }

export function MaterialRequestEditor() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const materials = useMaterialsList()
  const [lines, setLines] = useState<Line[]>([{ material_id: '', quantity: '' }])
  const [notes, setNotes] = useState('')

  const create = useMutation({
    mutationFn: async () => {
      const validLines = lines.filter(
        (l) => l.material_id && Number(l.quantity) > 0,
      )
      // Backend batch endpoint requires shared category, so group by it.
      const byCategory = new Map<
        string,
        Array<{ material_id: string; quantity: number; unit: string; notes?: string }>
      >()
      for (const l of validLines) {
        const m = (materials.data ?? []).find((mm) => mm.id === l.material_id)
        if (!m) continue
        const cat = m.category ?? 'Other'
        const list = byCategory.get(cat) ?? []
        list.push({
          material_id: l.material_id,
          quantity: Number(l.quantity),
          unit: m.unit ?? 'unit',
          notes: l.notes || notes || undefined,
        })
        byCategory.set(cat, list)
      }
      if (byCategory.size === 0) {
        throw new Error('Add at least one material with quantity > 0.')
      }
      // Post a separate batch per category. Demo size: typically 1–2 batches.
      for (const [category, items] of byCategory) {
        await fetchJson(orgPath('/material-requests/batch'), {
          method: 'POST',
          body: JSON.stringify({
            project_id: projectId,
            category,
            items,
          }),
        })
      }
    },
    onError: flagIfAuthError,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-requests', projectId] })
      navigate('..', { relative: 'path' })
    },
  })

  if (materials.isLoading) return <Loading />
  if (materials.isError) return <ErrorBanner error={materials.error} />

  return (
    <div>
      <PageHeader title="New material request" subtitle="One request, many lines." />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          {lines.map((line, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-6">
                <Field label="Material">
                  <Select
                    value={line.material_id}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((l, i) =>
                          i === idx ? { ...l, material_id: e.target.value } : l,
                        ),
                      )
                    }
                  >
                    <option value="">Select…</option>
                    {(materials.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} {m.unit ? `(${m.unit})` : ''}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-3">
                <Field label="Quantity">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={line.quantity}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((l, i) =>
                          i === idx ? { ...l, quantity: e.target.value } : l,
                        ),
                      )
                    }
                    required
                  />
                </Field>
              </div>
              <div className="sm:col-span-3 flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    setLines((prev) => [...prev, { material_id: '', quantity: '' }])
                  }
                >
                  + Line
                </Button>
                {lines.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}

          <Field label="Notes (optional)">
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          {create.isError && <ErrorBanner error={create.error} />}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
